import {
  Component,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { Button, Spinner } from "../ui";
import { api } from "../../api";
import { surfaceQuotaExhausted } from "../../utils/aiError";
import {
  type CreateWorkItemRequest,
  type Priority,
  type WorkItemType,
} from "../../api/types";
import {
  triggerAnalysis,
  getAnalysis,
  updateArchitectureSummary,
  resetArchitectureSummary,
  acceptBusinessInference,
  dismissDebtFlag,
  type RepoAnalysis,
  type RepoAnalysisVersion,
  type RepoBusinessInference,
  type RepoComponent,
  type RepoDebtFlag,
  type ActiveWork,
} from "../../api/repoAnalysis";

export interface RepositoryOverviewProps {
  projectId: string;
  repoId: string;
  repoName: string;
}

function formatRelativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function formatLoc(loc: number): string {
  if (loc >= 1000) return `${(loc / 1000).toFixed(1)}k`;
  return String(loc);
}

export const RepositoryOverview: Component<RepositoryOverviewProps> = (
  props,
) => {
  const [analysis, setAnalysis] = createSignal<RepoAnalysis | null>(null);
  const [version, setVersion] = createSignal<RepoAnalysisVersion | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [analyzing, setAnalyzing] = createSignal(false);
  const [fetchError, setFetchError] = createSignal<string | null>(null);
  const [expanded, setExpanded] = createSignal(false);
  const [editing, setEditing] = createSignal(false);
  const [editText, setEditText] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [hasUserEdits, setHasUserEdits] = createSignal(false);
  const [showResetConfirm, setShowResetConfirm] = createSignal(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = createSignal(false);
  const [inference, setInference] = createSignal<RepoBusinessInference | null>(
    null,
  );
  const [acceptingFields, setAcceptingFields] = createSignal<string[]>([]);
  const [components, setComponents] = createSignal<RepoComponent[]>([]);
  const [sortBy, setSortBy] = createSignal<"loc" | "complexity" | "name">(
    "loc",
  );
  const [activeWork, setActiveWork] = createSignal<ActiveWork | null>(null);
  const [activityExpanded, setActivityExpanded] = createSignal(false);
  const [debtFlags, setDebtFlags] = createSignal<RepoDebtFlag[]>([]);
  const [debtFilter, setDebtFilter] = createSignal<string>("all");
  const [dismissingFlag, setDismissingFlag] = createSignal<string | null>(null);
  const [creatingFlag, setCreatingFlag] = createSignal<string | null>(null);
  const [createdFlags, setCreatedFlags] = createSignal<Set<string>>(new Set());
  const [itemTypes, setItemTypes] = createSignal<WorkItemType[]>([]);

  const filteredDebtFlags = createMemo(() => {
    const filter = debtFilter();
    const flags = debtFlags();
    if (filter === "all") return flags;
    return flags.filter((f) => f.severity === filter);
  });

  const debtSummary = createMemo(() => {
    const flags = debtFlags();
    const critical = flags.filter((f) => f.severity === "critical").length;
    const high = flags.filter((f) => f.severity === "high").length;
    const medium = flags.filter((f) => f.severity === "medium").length;
    const low = flags.filter((f) => f.severity === "low").length;
    return { critical, high, medium, low, total: flags.length };
  });

  const sortedComponents = createMemo(() => {
    const comps = [...components()];
    const by = sortBy();
    if (by === "loc")
      return comps.sort((a, b) => (b.line_count ?? 0) - (a.line_count ?? 0));
    if (by === "complexity") {
      const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
      return comps.sort(
        (a, b) =>
          (order[a.complexity ?? "low"] ?? 2) -
          (order[b.complexity ?? "low"] ?? 2),
      );
    }
    return comps.sort((a, b) => a.module_name.localeCompare(b.module_name));
  });

  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  const loadAnalysis = async () => {
    try {
      const result = await getAnalysis(props.projectId, props.repoId);
      setAnalysis(result.analysis);
      setVersion(result.current_version);
      setHasUserEdits(result.has_user_edits);
      setInference(result.business_inference ?? null);
      setComponents(result.components ?? []);
      setActiveWork(result.analysis.active_work ?? null);
      setDebtFlags(result.debt_flags ?? []);
      setFetchError(null);

      if (result.analysis.status === "running") {
        if (pollTimer === undefined) {
          pollTimer = setInterval(async () => {
            try {
              const poll = await getAnalysis(props.projectId, props.repoId);
              setAnalysis(poll.analysis);
              setVersion(poll.current_version);
              setHasUserEdits(poll.has_user_edits);
              setInference(poll.business_inference ?? null);
              setComponents(poll.components ?? []);
              setActiveWork(poll.analysis.active_work ?? null);
              setDebtFlags(poll.debt_flags ?? []);
              if (poll.analysis.status !== "running") {
                stopPolling();
                setAnalyzing(false);
              }
            } catch {
              stopPolling();
              setAnalyzing(false);
            }
          }, 3000);
        }
      }
    } catch {
      // No analysis exists yet — not an error, just show empty state
      setAnalysis(null);
      setVersion(null);
    }
  };

  onMount(async () => {
    await loadAnalysis();
    try {
      const types = await api.get<WorkItemType[]>(
        `/projects/${props.projectId}/work-items/types`,
      );
      setItemTypes(types);
    } catch {
      // Item types are optional for this feature
    }
    setLoading(false);
  });

  onCleanup(() => stopPolling());

  const handleTrigger = async () => {
    setAnalyzing(true);
    setFetchError(null);
    try {
      const result = await triggerAnalysis(props.projectId, props.repoId);
      setAnalysis(result);
      setVersion(null);
      pollTimer = setInterval(async () => {
        try {
          const poll = await getAnalysis(props.projectId, props.repoId);
          setAnalysis(poll.analysis);
          setVersion(poll.current_version);
          setHasUserEdits(poll.has_user_edits);
          setInference(poll.business_inference ?? null);
          setComponents(poll.components ?? []);
          setActiveWork(poll.analysis.active_work ?? null);
          if (poll.analysis.status !== "running") {
            stopPolling();
            setAnalyzing(false);
          }
        } catch {
          stopPolling();
          setAnalyzing(false);
        }
      }, 3000);
    } catch (e) {
      surfaceQuotaExhausted(e);
      setFetchError(
        e instanceof Error ? e.message : "Failed to start analysis",
      );
      setAnalyzing(false);
    }
  };

  const handleEdit = () => {
    setEditText(version()?.architecture_summary ?? "");
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateArchitectureSummary(
        props.projectId,
        props.repoId,
        editText(),
      );
      setAnalysis(result.analysis);
      setVersion(result.current_version);
      setHasUserEdits(result.has_user_edits);
      setEditing(false);
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const result = await resetArchitectureSummary(
        props.projectId,
        props.repoId,
      );
      setAnalysis(result.analysis);
      setVersion(result.current_version);
      setHasUserEdits(result.has_user_edits);
      setShowResetConfirm(false);
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptField = async (field: string) => {
    setAcceptingFields((prev) => [...prev, field]);
    try {
      const result = await acceptBusinessInference(
        props.projectId,
        props.repoId,
        [field],
      );
      setInference(result);
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : "Failed to accept field");
    } finally {
      setAcceptingFields((prev) => prev.filter((f) => f !== field));
    }
  };

  const handleAcceptAll = async () => {
    const inf = inference();
    if (!inf) return;
    // Only include fields that are actually visible in the UI
    const visibleFields = ["purpose"];
    if (inf.target_audience !== null) visibleFields.push("audience");
    if (inf.inferred_objectives.length > 0) visibleFields.push("objectives");
    if (inf.domain_terms.length > 0) visibleFields.push("domain_terms");
    const unaccepted = visibleFields.filter(
      (f) => !inf.accepted_fields.includes(f),
    );
    if (unaccepted.length === 0) return;
    setAcceptingFields(visibleFields);
    try {
      const result = await acceptBusinessInference(
        props.projectId,
        props.repoId,
        unaccepted,
      );
      setInference(result);
    } catch (e) {
      setFetchError(
        e instanceof Error ? e.message : "Failed to accept all fields",
      );
    } finally {
      setAcceptingFields([]);
    }
  };

  const handleDismissFlag = async (flagId: string) => {
    setDismissingFlag(flagId);
    try {
      await dismissDebtFlag(props.projectId, props.repoId, flagId);
      setDebtFlags((prev) => prev.filter((f) => f.id !== flagId));
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : "Failed to dismiss flag");
    } finally {
      setDismissingFlag(null);
    }
  };

  const taskTypeId = createMemo(() => {
    const types = itemTypes();
    const task = types.find((t) => t.name.toLowerCase() === "task");
    return task?.id ?? types[0]?.id;
  });

  const handleCreateWorkItem = async (flag: RepoDebtFlag) => {
    const typeId = taskTypeId();
    if (!typeId) return;

    setCreatingFlag(flag.id);
    try {
      const severityToPriority: Record<string, Priority> = {
        critical: "high",
        high: "medium",
        medium: "low",
        low: "none",
      };

      const title = `[Tech Debt] ${flag.debt_type.replace(/_/g, " ")}: ${flag.location || "general"}`;
      const description = [
        flag.description,
        "",
        `**Severity:** ${flag.severity}`,
        `**Type:** ${flag.debt_type.replace(/_/g, " ")}`,
        flag.location ? `**Location:** ${flag.location}` : null,
        "",
        "_Created from repository analysis technical debt detection._",
      ]
        .filter(Boolean)
        .join("\n");

      const payload: CreateWorkItemRequest = {
        title,
        description,
        item_type_id: typeId,
        priority: severityToPriority[flag.severity] ?? "none",
      };

      await api.post(`/projects/${props.projectId}/work-items`, payload);
      setCreatedFlags((prev) => {
        const next = new Set(prev);
        next.add(flag.id);
        return next;
      });
    } catch (e) {
      setFetchError(
        e instanceof Error ? e.message : "Failed to create work item",
      );
    } finally {
      setCreatingFlag(null);
    }
  };

  return (
    <section class="mb-8">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-semibold text-ink">Repository Overview</h2>
          <p class="text-sm text-ink-3 mt-0.5">
            Powered by{" "}
            <span class="font-medium text-ink-2">{props.repoName}</span>
          </p>
        </div>
        <Show when={analysis()?.status === "complete"}>
          <div class="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (hasUserEdits()) {
                  setShowRegenerateConfirm(true);
                } else {
                  handleTrigger();
                }
              }}
              disabled={analyzing()}
              loading={analyzing()}
            >
              Regenerate
            </Button>
            <Show when={showRegenerateConfirm()}>
              <div class="absolute right-0 top-full mt-2 bg-surface border border-line rounded shadow-overlay p-3 z-10 w-64">
                <p class="text-sm text-ink-2 mb-2">
                  You have edited the summary. Regenerating will create a new AI
                  version.
                </p>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="px-3 py-1 bg-ink text-paper text-sm rounded focus-ring hover:bg-ink-2"
                    onClick={() => {
                      setShowRegenerateConfirm(false);
                      handleTrigger();
                    }}
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1 text-sm text-ink-2"
                    onClick={() => setShowRegenerateConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </Show>
      </div>

      <div class="bg-surface rounded border border-line p-5">
        <Show
          when={!loading()}
          fallback={
            <div class="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          }
        >
          {/* Running state */}
          <Show when={analyzing() || analysis()?.status === "running"}>
            <div class="flex items-center gap-3 py-2 text-ink-2">
              <Spinner size="sm" />
              <span class="text-sm">Analyzing {props.repoName}...</span>
            </div>
          </Show>

          {/* No analysis yet */}
          <Show
            when={
              !analyzing() &&
              (!analysis() || analysis()?.status === "pending") &&
              analysis()?.status !== "running"
            }
          >
            <div class="text-center py-6 space-y-3">
              <p class="text-sm text-ink-3">
                No analysis yet for{" "}
                <span class="font-medium">{props.repoName}</span>.
              </p>
              <p class="text-sm text-ink-3 max-w-md mx-auto">
                Flowicity will scan your repository and generate an architecture
                summary to help the AI understand your codebase. This typically
                takes 30-60 seconds.
              </p>
              <Button
                variant="primary"
                onClick={handleTrigger}
                disabled={analyzing()}
              >
                Analyze Repository
              </Button>
              <Show when={fetchError()}>
                <p class="text-sm text-signal-red">{fetchError()}</p>
              </Show>
            </div>
          </Show>

          {/* Failed state */}
          <Show when={!analyzing() && analysis()?.status === "failed"}>
            <div class="space-y-3">
              <p class="text-sm text-signal-red">
                Analysis failed
                <Show when={analysis()?.error_message}>
                  {": "}
                  {analysis()?.error_message}
                </Show>
              </p>
              <button
                type="button"
                class="inline-flex items-center px-3 py-1.5 bg-ink text-paper text-sm rounded focus-ring hover:bg-ink-2 disabled:opacity-50"
                onClick={handleTrigger}
                disabled={analyzing()}
              >
                Retry
              </button>
              <Show when={fetchError()}>
                <p class="text-sm text-signal-red">{fetchError()}</p>
              </Show>
            </div>
          </Show>

          {/* Complete state */}
          <Show
            when={
              !analyzing() && analysis()?.status === "complete" && version()
            }
          >
            <div class="space-y-3">
              {/* Badge row */}
              <div class="flex items-center gap-2">
                <Show
                  when={version()?.version_source === "user_edit"}
                  fallback={
                    <span class="text-xs bg-signal-magenta-tint text-signal-magenta px-1.5 py-0.5 rounded font-medium">
                      AI-generated
                    </span>
                  }
                >
                  <span class="text-xs bg-signal-amber-tint text-signal-amber px-1.5 py-0.5 rounded font-medium">
                    Edited
                  </span>
                </Show>
                <Show when={analysis()?.analyzed_at}>
                  <span class="text-xs text-ink-3">
                    Analyzed {formatRelativeTime(analysis()?.analyzed_at ?? "")}
                  </span>
                </Show>
              </div>

              {/* Edit mode vs read mode */}
              <Show
                when={editing()}
                fallback={
                  <div>
                    <div
                      class={`text-sm text-ink-2 whitespace-pre-wrap font-sans leading-relaxed bg-sunken rounded p-4 border border-line overflow-hidden ${expanded() ? "" : "max-h-64"}`}
                    >
                      {version()?.architecture_summary}
                    </div>
                    <div class="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        class="text-sm text-ink-2 hover:text-ink font-medium"
                        onClick={() => setExpanded(!expanded())}
                      >
                        {expanded() ? "Show less" : "Show more"}
                      </button>
                      <button
                        type="button"
                        class="text-sm text-ink-2 hover:text-ink font-medium"
                        onClick={handleEdit}
                      >
                        Edit
                      </button>
                      <Show when={version()?.version_source === "user_edit"}>
                        <button
                          type="button"
                          class="text-sm text-ink-3 hover:text-ink"
                          onClick={() => setShowResetConfirm(true)}
                        >
                          Reset to AI version
                        </button>
                      </Show>
                    </div>
                  </div>
                }
              >
                <div>
                  <textarea
                    class="w-full text-sm text-ink-2 font-sans leading-relaxed bg-surface rounded p-4 border border-line focus-ring resize-y"
                    rows={12}
                    value={editText()}
                    onInput={(e) => setEditText(e.currentTarget.value)}
                  />
                  <div class="flex items-center gap-3">
                    <button
                      type="button"
                      class="inline-flex items-center px-3 py-1.5 bg-ink text-paper text-sm rounded focus-ring hover:bg-ink-2 disabled:opacity-50"
                      onClick={handleSave}
                      disabled={saving()}
                    >
                      {saving() ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      class="text-sm text-ink-3 hover:text-ink"
                      onClick={() => setEditing(false)}
                      disabled={saving()}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Show>

              {/* Reset confirmation */}
              <Show when={showResetConfirm()}>
                <div class="bg-signal-amber-tint border border-signal-amber/25 rounded p-3 text-sm">
                  <p class="text-signal-amber mb-2">
                    Reset to the AI-generated version? Your edits will no longer
                    be the active summary.
                  </p>
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="px-3 py-1 bg-surface text-ink border border-line text-sm rounded focus-ring hover:bg-sunken hover:border-line-strong"
                      onClick={handleReset}
                      disabled={saving()}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      class="px-3 py-1 text-sm text-ink-2 hover:text-ink"
                      onClick={() => setShowResetConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Show>

              {/* Error display */}
              <Show when={fetchError()}>
                <p class="text-sm text-signal-red">{fetchError()}</p>
              </Show>
            </div>
          </Show>
        </Show>
      </div>

      {/* Current Activity section */}
      <Show
        when={
          !analyzing() &&
          analysis()?.status === "complete" &&
          activeWork() &&
          ((activeWork()?.focus_areas?.length ?? 0) > 0 ||
            (activeWork()?.contributors?.length ?? 0) > 0 ||
            (activeWork()?.stale_items?.length ?? 0) > 0)
            ? activeWork()
            : undefined
        }
        keyed
      >
        {(work) => (
          <div class="bg-surface rounded border border-line p-5 mt-4">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="text-sm font-semibold text-ink">Current Activity</h3>
                <p class="text-xs text-ink-3 mt-0.5">
                  Inferred from recent Git activity
                </p>
              </div>
              <button
                type="button"
                class="text-xs text-ink-2 hover:text-ink font-medium"
                onClick={() => setActivityExpanded(!activityExpanded())}
                aria-expanded={activityExpanded()}
                aria-controls="current-activity-body"
              >
                {activityExpanded() ? "Show less" : "Show more"}
              </button>
            </div>

            <div id="current-activity-body">
              {/* Focus Areas */}
              <Show when={(work.focus_areas?.length ?? 0) > 0}>
                <div class="mb-4">
                  <p class="text-xs font-medium text-ink-3 uppercase tracking-wide mb-2">
                    Focus Areas
                  </p>
                  <div
                    class={`space-y-2 overflow-hidden ${activityExpanded() ? "" : "max-h-48"}`}
                  >
                    <For each={work.focus_areas}>
                      {(area) => (
                        <div class="border border-line rounded p-3 bg-sunken">
                          <p class="text-sm font-medium text-ink">
                            {area.name}
                          </p>
                          <p class="text-xs text-ink-3 mt-0.5">
                            {area.description}
                          </p>
                          <Show
                            when={
                              (area.evidence?.commits?.length ?? 0) > 0 ||
                              (area.evidence?.pull_requests?.length ?? 0) > 0
                            }
                          >
                            <div class="mt-2 space-y-1">
                              <Show
                                when={(area.evidence?.commits?.length ?? 0) > 0}
                              >
                                <div>
                                  <p class="text-xs text-ink-3 font-medium mb-0.5">
                                    Commits
                                  </p>
                                  <ul
                                    class="space-y-0.5"
                                    role="list"
                                    aria-label="Supporting commits"
                                  >
                                    <For each={area.evidence?.commits ?? []}>
                                      {(commit) => (
                                        <li class="text-xs text-ink-2 pl-2 border-l-2 border-line">
                                          {commit}
                                        </li>
                                      )}
                                    </For>
                                  </ul>
                                </div>
                              </Show>
                              <Show
                                when={
                                  (area.evidence?.pull_requests?.length ?? 0) >
                                  0
                                }
                              >
                                <div>
                                  <p class="text-xs text-ink-3 font-medium mb-0.5">
                                    Pull Requests
                                  </p>
                                  <ul
                                    class="space-y-0.5"
                                    role="list"
                                    aria-label="Supporting pull requests"
                                  >
                                    <For
                                      each={area.evidence?.pull_requests ?? []}
                                    >
                                      {(pr) => (
                                        <li class="text-xs text-ink-2 pl-2 border-l-2 border-line">
                                          {pr}
                                        </li>
                                      )}
                                    </For>
                                  </ul>
                                </div>
                              </Show>
                            </div>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              {/* Contributors */}
              <Show when={(work.contributors?.length ?? 0) > 0}>
                <div class="mb-4">
                  <p class="text-xs font-medium text-ink-3 uppercase tracking-wide mb-2">
                    Contributors
                  </p>
                  <div class="divide-y divide-line">
                    <For each={work.contributors}>
                      {(contributor) => (
                        <div class="flex items-start justify-between py-2">
                          <span class="text-sm font-medium text-ink-2">
                            {contributor.name}
                          </span>
                          <div
                            class="flex flex-wrap gap-1 justify-end ml-3"
                            aria-label={`Active areas for ${contributor.name}`}
                          >
                            <For each={contributor.active_areas ?? []}>
                              {(area) => (
                                <span class="text-xs bg-sunken text-ink-2 border border-line font-mono px-1.5 py-0.5 rounded-full">
                                  {area}
                                </span>
                              )}
                            </For>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              {/* Stale Items */}
              <Show when={(work.stale_items?.length ?? 0) > 0}>
                <div>
                  <p class="text-xs font-medium text-ink-3 uppercase tracking-wide mb-2">
                    Stale Items
                  </p>
                  <div class="space-y-1.5">
                    <For each={work.stale_items}>
                      {(item) => (
                        <div
                          class="flex items-start gap-2 bg-signal-amber-tint border border-signal-amber/25 rounded px-3 py-2"
                          role="alert"
                          aria-label={`Stale ${item.type}: ${item.title}`}
                        >
                          <span class="text-xs font-medium text-signal-amber uppercase shrink-0 mt-0.5">
                            {item.type}
                          </span>
                          <div class="min-w-0 flex-1">
                            <p class="text-xs font-medium text-signal-amber truncate">
                              {item.title}
                            </p>
                            <p class="text-xs text-signal-amber mt-0.5">
                              {item.reason}
                            </p>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          </div>
        )}
      </Show>

      {/* Component Inventory section */}
      <Show
        when={
          !analyzing() &&
          analysis()?.status === "complete" &&
          components().length > 0
        }
      >
        <div class="bg-surface rounded border border-line p-5 mt-4">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-sm font-semibold text-ink">
                Component Inventory
              </h3>
              <p class="text-xs text-ink-3 mt-0.5">
                {components().length} modules identified
              </p>
            </div>
            <div class="flex items-center gap-1 text-xs text-ink-3">
              <span>Sort by:</span>
              <button
                type="button"
                class={`px-2 py-1 rounded border ${sortBy() === "loc" ? "bg-ink text-paper border-ink font-medium" : "bg-surface text-ink-2 border-line hover:bg-sunken"}`}
                onClick={() => setSortBy("loc")}
              >
                Size
              </button>
              <button
                type="button"
                class={`px-2 py-1 rounded border ${sortBy() === "complexity" ? "bg-ink text-paper border-ink font-medium" : "bg-surface text-ink-2 border-line hover:bg-sunken"}`}
                onClick={() => setSortBy("complexity")}
              >
                Complexity
              </button>
              <button
                type="button"
                class={`px-2 py-1 rounded border ${sortBy() === "name" ? "bg-ink text-paper border-ink font-medium" : "bg-surface text-ink-2 border-line hover:bg-sunken"}`}
                onClick={() => setSortBy("name")}
              >
                Name
              </button>
            </div>
          </div>

          <div class="divide-y divide-line">
            <For each={sortedComponents()}>
              {(component) => (
                <div class="flex items-center justify-between py-2.5">
                  <div class="min-w-0 flex-1">
                    <p
                      class="text-sm font-medium text-ink truncate"
                      title={component.file_path}
                    >
                      {component.module_name}
                    </p>
                    <p class="text-xs text-ink-3 truncate">
                      {component.file_path}
                    </p>
                  </div>
                  <div class="flex items-center gap-2 ml-3 shrink-0">
                    <Show when={component.language}>
                      <span class="text-xs text-ink-3">
                        {component.language}
                      </span>
                    </Show>
                    <Show when={component.line_count != null}>
                      <span class="text-xs bg-sunken text-ink-2 px-1.5 py-0.5 rounded font-mono">
                        {formatLoc(component.line_count as number)}
                      </span>
                    </Show>
                    <ComplexityBadge complexity={component.complexity} />
                    <Show
                      when={component.has_tests}
                      fallback={
                        <span
                          class="text-xs text-ink-3"
                          title="No tests detected"
                        >
                          no tests
                        </span>
                      }
                    >
                      <span
                        class="text-xs text-signal-green font-medium"
                        title="Tests detected"
                      >
                        ✓ tests
                      </span>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* Business Context section — only shown when analysis is complete and inference exists */}
      <Show
        when={!analyzing() && analysis()?.status === "complete" && inference()}
      >
        {(inf) => (
          <div class="bg-surface rounded border border-line p-5 mt-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-sm font-semibold text-ink">Business Context</h3>
              <span class="text-xs text-ink-3">AI-inferred</span>
            </div>

            <p class="text-xs text-ink-3 mb-3">
              Accepting a field adds it to your project's AI context.
            </p>

            <div class="space-y-3">
              {/* Purpose */}
              <div class="border border-line rounded p-3 bg-sunken">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-medium text-ink-3 uppercase tracking-wide">
                    Project Purpose
                  </span>
                  <div class="flex items-center gap-2">
                    <ConfidenceBadge confidence={inf().purpose_confidence} />
                    <Show
                      when={inf().accepted_fields.includes("purpose")}
                      fallback={
                        <button
                          type="button"
                          class="text-xs text-ink-2 hover:text-ink font-medium disabled:opacity-50"
                          onClick={() => handleAcceptField("purpose")}
                          disabled={acceptingFields().includes("purpose")}
                          aria-label="Accept purpose into project context"
                        >
                          {acceptingFields().includes("purpose")
                            ? "Adding..."
                            : "Add to context"}
                        </button>
                      }
                    >
                      <span
                        class="text-xs text-signal-green font-medium"
                        aria-label="Purpose accepted"
                      >
                        Accepted
                      </span>
                    </Show>
                  </div>
                </div>
                <p class="text-sm text-ink-2">{inf().inferred_purpose}</p>
              </div>

              {/* Audience */}
              <Show when={inf().target_audience !== null}>
                <div class="border border-line rounded p-3 bg-sunken">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-medium text-ink-3 uppercase tracking-wide">
                      Target Audience
                    </span>
                    <div class="flex items-center gap-2">
                      <ConfidenceBadge confidence={inf().audience_confidence} />
                      <Show
                        when={inf().accepted_fields.includes("audience")}
                        fallback={
                          <button
                            type="button"
                            class="text-xs text-ink-2 hover:text-ink font-medium disabled:opacity-50"
                            onClick={() => handleAcceptField("audience")}
                            disabled={acceptingFields().includes("audience")}
                            aria-label="Accept audience into project context"
                          >
                            {acceptingFields().includes("audience")
                              ? "Accepting..."
                              : "Accept"}
                          </button>
                        }
                      >
                        <span
                          class="text-xs text-signal-green font-medium"
                          aria-label="Audience accepted"
                        >
                          Accepted
                        </span>
                      </Show>
                    </div>
                  </div>
                  <p class="text-sm text-ink-2">{inf().target_audience}</p>
                </div>
              </Show>

              {/* Objectives */}
              <Show when={inf().inferred_objectives.length > 0}>
                <div class="border border-line rounded p-3 bg-sunken">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-medium text-ink-3 uppercase tracking-wide">
                      Key Objectives
                    </span>
                    <Show
                      when={inf().accepted_fields.includes("objectives")}
                      fallback={
                        <button
                          type="button"
                          class="text-xs text-ink-2 hover:text-ink font-medium disabled:opacity-50"
                          onClick={() => handleAcceptField("objectives")}
                          disabled={acceptingFields().includes("objectives")}
                          aria-label="Accept objectives into project context"
                        >
                          {acceptingFields().includes("objectives")
                            ? "Adding..."
                            : "Add to context"}
                        </button>
                      }
                    >
                      <span
                        class="text-xs text-signal-green font-medium"
                        aria-label="Objectives accepted"
                      >
                        Accepted
                      </span>
                    </Show>
                  </div>
                  <ul class="space-y-1" role="list">
                    <For each={inf().inferred_objectives}>
                      {(obj) => (
                        <li class="flex items-center justify-between text-sm text-ink-2">
                          <span>{obj.text}</span>
                          <ConfidenceBadge confidence={obj.confidence} />
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>

              {/* Domain Terms */}
              <Show when={inf().domain_terms.length > 0}>
                <div class="border border-line rounded p-3 bg-sunken">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-medium text-ink-3 uppercase tracking-wide">
                      Domain Terms
                    </span>
                    <Show
                      when={inf().accepted_fields.includes("domain_terms")}
                      fallback={
                        <button
                          type="button"
                          class="text-xs text-ink-2 hover:text-ink font-medium disabled:opacity-50"
                          onClick={() => handleAcceptField("domain_terms")}
                          disabled={acceptingFields().includes("domain_terms")}
                          aria-label="Accept domain terms into project context"
                        >
                          {acceptingFields().includes("domain_terms")
                            ? "Adding..."
                            : "Add to context"}
                        </button>
                      }
                    >
                      <span
                        class="text-xs text-signal-green font-medium"
                        aria-label="Domain terms accepted"
                      >
                        Accepted
                      </span>
                    </Show>
                  </div>
                  <div
                    class="flex flex-wrap gap-1.5"
                    role="list"
                    aria-label="Domain terms"
                  >
                    <For each={inf().domain_terms}>
                      {(term) => (
                        <span
                          role="listitem"
                          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-line text-xs text-ink-2"
                        >
                          {term.term}
                          <ConfidenceBadge confidence={term.confidence} />
                        </span>
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              {/* Accept All button */}
              <Show
                when={(() => {
                  const i = inf();
                  const visible = ["purpose"];
                  if (i.target_audience !== null) visible.push("audience");
                  if (i.inferred_objectives.length > 0)
                    visible.push("objectives");
                  if (i.domain_terms.length > 0) visible.push("domain_terms");
                  return visible.every((f) => i.accepted_fields.includes(f));
                })()}
                fallback={
                  <button
                    type="button"
                    class="w-full py-2 px-4 text-sm font-medium text-ink bg-surface border border-line rounded focus-ring hover:bg-sunken hover:border-line-strong disabled:opacity-50"
                    onClick={handleAcceptAll}
                    disabled={acceptingFields().length > 0}
                  >
                    {acceptingFields().length > 0
                      ? "Adding..."
                      : "Add All to Project Context"}
                  </button>
                }
              >
                <p class="text-center text-xs text-signal-green font-medium py-1">
                  All context accepted
                </p>
              </Show>

              {/* Error display for accept actions */}
              <Show when={fetchError()}>
                <p class="text-sm text-signal-red">{fetchError()}</p>
              </Show>
            </div>
          </div>
        )}
      </Show>

      {/* Technical Debt section */}
      <Show
        when={
          !analyzing() &&
          analysis()?.status === "complete" &&
          debtFlags().length > 0
        }
      >
        <div class="bg-surface rounded border border-line p-5 mt-4">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-sm font-semibold text-ink">Technical Debt</h3>
              <p class="text-xs text-ink-3 mt-0.5">
                {debtSummary().total} item{debtSummary().total !== 1 ? "s" : ""}{" "}
                detected
                <Show when={debtSummary().critical > 0}>
                  {" "}
                  &middot;{" "}
                  <span class="text-signal-red font-medium">
                    {debtSummary().critical} critical
                  </span>
                </Show>
                <Show when={debtSummary().high > 0}>
                  {" "}
                  &middot;{" "}
                  <span class="text-signal-amber font-medium">
                    {debtSummary().high} high
                  </span>
                </Show>
              </p>
            </div>
            <div class="flex items-center gap-1 text-xs text-ink-3">
              <span>Filter:</span>
              <For each={["all", "critical", "high", "medium", "low"] as const}>
                {(level) => (
                  <button
                    type="button"
                    class={`px-2 py-1 rounded border capitalize ${
                      debtFilter() === level
                        ? "bg-ink text-paper border-ink font-medium"
                        : "bg-surface text-ink-2 border-line hover:bg-sunken"
                    }`}
                    onClick={() => setDebtFilter(level)}
                  >
                    {level}
                  </button>
                )}
              </For>
            </div>
          </div>

          <div class="divide-y divide-line">
            <For each={filteredDebtFlags()}>
              {(flag) => (
                <div class="flex items-start justify-between py-2.5 gap-3">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2 mb-1">
                      <SeverityBadge severity={flag.severity} />
                      <DebtTypeBadge type={flag.debt_type} />
                      <Show when={flag.location} keyed>
                        {(loc) => (
                          <span class="text-xs text-ink-3 truncate" title={loc}>
                            {loc}
                          </span>
                        )}
                      </Show>
                    </div>
                    <p class="text-sm text-ink-2">{flag.description}</p>
                  </div>
                  <div class="flex items-center gap-1 shrink-0">
                    <Show
                      when={!createdFlags().has(flag.id)}
                      fallback={
                        <span class="text-xs text-signal-green font-medium px-2 py-1">
                          Created
                        </span>
                      }
                    >
                      <button
                        type="button"
                        class="text-xs text-ink-2 hover:text-ink hover:bg-sunken rounded px-2 py-1 font-medium"
                        onClick={() => handleCreateWorkItem(flag)}
                        disabled={creatingFlag() !== null}
                        title="Create a work item to track this debt"
                      >
                        {creatingFlag() === flag.id
                          ? "Creating..."
                          : "Create Item"}
                      </button>
                    </Show>
                    <button
                      type="button"
                      class="text-xs text-ink-3 hover:text-ink shrink-0 px-2 py-1"
                      onClick={() => handleDismissFlag(flag.id)}
                      disabled={
                        dismissingFlag() === flag.id ||
                        creatingFlag() === flag.id
                      }
                      title="Dismiss this flag"
                    >
                      {dismissingFlag() === flag.id ? "..." : "Dismiss"}
                    </button>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>
    </section>
  );
};

function ComplexityBadge(props: {
  complexity: "low" | "medium" | "high" | null;
}) {
  const badgeClass = () => {
    if (props.complexity === "high")
      return "bg-signal-red-tint text-signal-red";
    if (props.complexity === "medium")
      return "bg-signal-amber-tint text-signal-amber";
    if (props.complexity === "low")
      return "bg-signal-green-tint text-signal-green";
    return "bg-sunken text-ink-3";
  };
  return (
    <Show when={props.complexity}>
      <span class={`text-xs px-1.5 py-0.5 rounded font-medium ${badgeClass()}`}>
        {props.complexity}
      </span>
    </Show>
  );
}

const SeverityBadge: Component<{ severity: string | null }> = (props) => {
  const classes = () => {
    switch (props.severity) {
      case "critical":
        return "bg-signal-red-tint text-signal-red";
      case "high":
        return "bg-signal-amber-tint text-signal-amber";
      case "medium":
        return "bg-signal-amber-tint text-ink-2 border border-signal-amber/25";
      default:
        return "bg-sunken text-ink-2";
    }
  };
  return (
    <span
      class={`text-xs px-1.5 py-0.5 rounded font-medium capitalize ${classes()}`}
    >
      {props.severity ?? "unknown"}
    </span>
  );
};

const DebtTypeBadge: Component<{ type: string }> = (props) => {
  const label = () => props.type.replace(/_/g, " ");
  return (
    <span class="text-xs bg-sunken text-ink-2 border border-line font-mono px-1.5 py-0.5 rounded">
      {label()}
    </span>
  );
};

function ConfidenceBadge(props: { confidence: string }) {
  const badgeClass = () => {
    if (props.confidence === "high")
      return "bg-signal-green-tint text-signal-green";
    if (props.confidence === "medium")
      return "bg-signal-amber-tint text-signal-amber";
    return "bg-surface text-ink-3 border border-line";
  };
  const tooltip = () => {
    if (props.confidence === "high")
      return "Strong evidence from repository signals";
    if (props.confidence === "medium")
      return "Reasonable inference from available signals";
    return "Speculative — limited evidence available";
  };
  return (
    <span
      class={`inline-block text-xs px-1.5 py-0.5 rounded font-medium cursor-help ${badgeClass()}`}
      title={tooltip()}
    >
      {props.confidence.toUpperCase()}
    </span>
  );
}
